import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import './LegalDashboard.css';
import Navbar from '../../shared/navbar';
import SuperDocDocumentManager from '../../components/SuperDocDocumentManager';

/* ──────────────────────────── helpers ──────────────────────────── */
const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n || 0);

const fmtDate = (d) => {
  if (!d) return '--';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const today = () => new Date().toISOString().split('T')[0];

const TABS = [
  { id: 'Dashboard', label: 'Dashboard', icon: '📊' },
  { id: 'Tenant Cards', label: 'Tenant Cards', icon: '👤' },
  { id: 'Legal Cases', label: 'Legal Cases', icon: '⚖️' },
  { id: 'Compliance', label: 'Compliance', icon: '✅' },
  { id: 'Documents', label: 'Documents', icon: '📄' },
  { id: 'Tenant Scoring', label: 'Tenant Scoring', icon: '📈' },
  { id: 'Notices & Evictions', label: 'Notices & Evictions', icon: '🚨' },
  { id: 'Lease Renewals', label: 'Lease Renewals', icon: '🔄' },
  { id: 'Inspections', label: 'Inspections', icon: '🔍' },
];

const EVICTION_STAGES = [
  { key: 'notice_to_cure', label: 'Notice to Cure', color: '#F59E0B' },
  { key: 'notice_to_quit', label: 'Notice to Quit', color: '#F97316' },
  { key: 'pay_or_quit', label: 'Pay or Quit', color: '#EA580C' },
  { key: 'ud_filed', label: 'UD Filed', color: '#EF4444' },
  { key: 'court_hearing', label: 'Court Hearing', color: '#8B5CF6' },
  { key: 'judgment', label: 'Judgment', color: '#991B1B' },
  { key: 'writ_lockout', label: 'Writ / Lockout', color: '#111827' },
  { key: 'resolved', label: 'Resolved', color: '#10B981' },
];

const CASE_TYPES = [
  { key: 'non_payment', label: 'Non-payment' },
  { key: 'lease_violation', label: 'Lease Violation' },
  { key: 'nuisance', label: 'Nuisance' },
  { key: 'illegal_activity', label: 'Illegal Activity' },
  { key: 'hoa_violation', label: 'HOA Violation' },
];

const VIOLATION_TYPES = [
  'Unauthorized Occupant', 'Unauthorized Pet', 'Excessive Noise',
  'Property Damage', 'Parking Violation', 'Sanitation Issue',
  'Unauthorized Modification', 'Other'
];

const SERVING_METHODS = [
  { key: 'in_person', label: 'In Person' },
  { key: 'certified_mail', label: 'Certified Mail' },
  { key: 'posted', label: 'Posted on Door' },
  { key: 'all_three', label: 'All Three (recommended)' },
];

const COMPLIANCE_CATEGORIES = [
  { key: 'fair_housing', label: 'Fair Housing', icon: '🏠', description: 'Anti-discrimination compliance, reasonable accommodations, equal opportunity housing' },
  { key: 'safety', label: 'Safety', icon: '🔥', description: 'Fire safety, smoke detectors, carbon monoxide, emergency exits, building codes' },
  { key: 'permits', label: 'Permits', icon: '📋', description: 'Rental permits, business licenses, certificates of occupancy, zoning compliance' },
  { key: 'ada', label: 'ADA', icon: '♿', description: 'Accessibility requirements, reasonable modifications, common area accessibility' },
  { key: 'environmental', label: 'Environmental', icon: '🌿', description: 'Lead paint disclosure, asbestos, mold remediation, environmental hazards' },
  { key: 'data_privacy', label: 'Data Privacy', icon: '🔒', description: 'Tenant data protection, record retention, privacy policy compliance' }
];

const TEMPLATE_DATA = {
  master: [
    { id: 'm1', title: 'Standard Lease Agreement', description: 'Base residential lease template with all required clauses', tier: 'master' },
    { id: 'm2', title: 'Eviction Notice Template', description: 'Legal eviction notice with proper statutory language', tier: 'master' },
    { id: 'm3', title: 'Lease Renewal Agreement', description: 'Standard renewal terms and conditions template', tier: 'master' },
    { id: 'm4', title: 'Notice to Cure', description: 'Cure or quit notice for lease violations', tier: 'master' },
    { id: 'm5', title: 'Move-In/Move-Out Inspection', description: 'Property condition documentation checklist', tier: 'master' },
    { id: 'm6', title: 'Security Deposit Return', description: 'Itemized security deposit accounting letter', tier: 'master' },
  ],
  property: [
    { id: 'p1', title: 'HOA Addendum', description: 'Property-specific HOA rules and regulations addendum', tier: 'property' },
    { id: 'p2', title: 'Parking Agreement', description: 'Property-specific parking space assignment and rules', tier: 'property' },
    { id: 'p3', title: 'Pet Policy Addendum', description: 'Property-specific pet rules, deposits, and breed restrictions', tier: 'property' },
  ],
  tenant: [
    { id: 't1', title: 'Payment Plan Agreement', description: 'Tenant-specific payment plan for outstanding balance', tier: 'tenant' },
    { id: 't2', title: 'Lease Modification', description: 'Individual lease term modification document', tier: 'tenant' },
    { id: 't3', title: 'Accommodation Agreement', description: 'Reasonable accommodation terms for specific tenant', tier: 'tenant' },
  ]
};

const statusColor = (status) => {
  if (!status) return '#6B7280';
  const s = status.toLowerCase();
  if (['critical', 'overdue', 'eviction', 'high', 'expired', 'lockout', 'rejected'].includes(s)) return '#EF4444';
  if (['warning', 'on_notice', 'medium', 'notice_to_quit', 'pending', 'notice_to_cure'].includes(s)) return '#F59E0B';
  if (['in-progress', 'in_progress', 'active', 'filing', 'court_hearing', 'open', 'review'].includes(s)) return '#3B82F6';
  if (['complete', 'compliant', 'good_standing', 'low', 'resolved', 'approved', 'renewed'].includes(s)) return '#10B981';
  return '#6B7280';
};

const statusBg = (status) => {
  const c = statusColor(status);
  return c + '18';
};

const scoreColor = (score) => {
  if (score < 40) return '#EF4444';
  if (score < 70) return '#F59E0B';
  return '#10B981';
};

/* ──────────────────────────── pagination hook ──────────────────── */
function paginate(items, page, perPage = 10) {
  const total = Math.ceil(items.length / perPage) || 1;
  const paginated = items.slice((page - 1) * perPage, page * perPage);
  return { total, paginated };
}

/* ──────────────────────────── sort hook ────────────────────────── */
function useSort(defaultKey, defaultDir = 'asc') {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);
  const toggle = (key) => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };
  const sortFn = useCallback((a, b) => {
    let va = a[sortKey], vb = b[sortKey];
    if (va == null) va = '';
    if (vb == null) vb = '';
    if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va;
    va = String(va).toLowerCase(); vb = String(vb).toLowerCase();
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  }, [sortKey, sortDir]);
  return { sortKey, sortDir, toggle, sortFn };
}

/* ──────────────────────────── SortHeader ───────────────────────── */
const SortHeader = ({ label, colKey, sortKey, sortDir, onSort }) => (
  <th onClick={() => onSort(colKey)} style={{ cursor: 'pointer', userSelect: 'none' }}>
    <span className="sort-header-inner">{label} {sortKey === colKey ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
  </th>
);

/* ──────────────────────────── Pagination ───────────────────────── */
const Pagination = ({ page, total, setPage }) => {
  if (total <= 1) return null;
  return (
    <div className="legal-pagination">
      <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
      <span className="page-info">Page {page} of {total}</span>
      <button disabled={page === total} onClick={() => setPage(p => p + 1)}>Next →</button>
    </div>
  );
};

/* ──────────────────────────── StatusBadge ──────────────────────── */
const StatusBadge = ({ status }) => {
  if (!status) return null;
  const display = status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return (
    <span className="legal-status-badge" style={{ color: statusColor(status), background: statusBg(status) }}>
      {display}
    </span>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════ */
const LegalDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'Dashboard');
  const navigate = useNavigate();

  /* global filter */
  const [properties, setProperties] = useState([]);
  const [tenantsList, setTenantsList] = useState([]);
  const [propertyFilter, setPropertyFilter] = useState('');

  /* dashboard */
  const [dashboardSummary, setDashboardSummary] = useState({ active_cases: 0, pending_evictions: 0, upcoming_renewals: 0, compliance_issues: 0, overdue_inspections: 0, total_legal_spend: 0 });
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [riskAssessment, setRiskAssessment] = useState({ high_risk: 0, medium_risk: 0, low_risk: 0 });
  const [calendarDate, setCalendarDate] = useState(new Date());

  /* tenant cards */
  const [tenantCards, setTenantCards] = useState([]);
  const [tenantCardsSearch, setTenantCardsSearch] = useState('');

  /* legal cases */
  const [cases, setCases] = useState([]);
  const [casesSearch, setCasesSearch] = useState('');
  const [casePanel, setCasePanel] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [caseForm, setCaseForm] = useState({});

  /* compliance */
  const [complianceItems, setComplianceItems] = useState([]);

  /* tenant scoring */
  const [scores, setScores] = useState([]);
  const [scoresSearch, setScoresSearch] = useState('');
  const [expandedScore, setExpandedScore] = useState(null);

  /* eviction notices (kanban) */
  const [kanbanData, setKanbanData] = useState({});
  const [dragOverStage, setDragOverStage] = useState(null);
  const [evictionPanel, setEvictionPanel] = useState(false);
  const [editingEviction, setEditingEviction] = useState(null);
  const [evictionForm, setEvictionForm] = useState({});

  /* eviction notices — views & filters */
  const [evView, setEvView] = useState('list');
  const [evFilterStage, setEvFilterStage] = useState('');
  const [evFilterCaseType, setEvFilterCaseType] = useState('');
  const [evFilterAssigned, setEvFilterAssigned] = useState('');
  const [evSortKey, setEvSortKey] = useState('due_date');
  const [evSortDir, setEvSortDir] = useState('asc');
  const [evFilterOpen, setEvFilterOpen] = useState(false);
  const [evSortOpen, setEvSortOpen] = useState(false);
  const [evStatFilter, setEvStatFilter] = useState('');

  /* eviction side panel */
  const [evSelectedNotice, setEvSelectedNotice] = useState(null);
  const [evPanelTab, setEvPanelTab] = useState('timeline');
  const [evNewNote, setEvNewNote] = useState('');
  const [evNewActionType, setEvNewActionType] = useState('');
  const [evNewActionNotes, setEvNewActionNotes] = useState('');
  const [evNewActionDate, setEvNewActionDate] = useState('');

  /* new notice modal */
  const [newNoticeModal, setNewNoticeModal] = useState(false);
  const [newNoticeStep, setNewNoticeStep] = useState(1);
  const [newNoticeData, setNewNoticeData] = useState({
    tenant_id: '', property_id: '', case_type: 'non_payment',
    amount_owed: '', months_overdue: '', last_payment_date: '',
    violation_type: '', violation_description: '', prior_warnings: false, warning_count: 0,
    cure_period: 14, serving_method: 'all_three', assigned_to: '',
    reason: '', notes: ''
  });
  const [tenantSearch, setTenantSearch] = useState('');

  /* calendar view */
  const [evCalDate, setEvCalDate] = useState(new Date());
  const [evCalSelectedDate, setEvCalSelectedDate] = useState(null);

  /* eviction list pagination */
  const [evPage, setEvPage] = useState(1);

  /* lease renewals */
  const [renewals, setRenewals] = useState([]);
  const [renewalsSearch, setRenewalsSearch] = useState('');
  const [renewalPanel, setRenewalPanel] = useState(false);
  const [editingRenewal, setEditingRenewal] = useState(null);
  const [renewalForm, setRenewalForm] = useState({});

  /* inspections */
  const [inspections, setInspections] = useState([]);
  const [inspectionsSearch, setInspectionsSearch] = useState('');
  const [inspectionPanel, setInspectionPanel] = useState(false);
  const [editingInspection, setEditingInspection] = useState(null);
  const [inspectionForm, setInspectionForm] = useState({});

  /* pagination pages */
  const [casesPage, setCasesPage] = useState(1);
  const [scoresPage, setScoresPage] = useState(1);
  const [renewalsPage, setRenewalsPage] = useState(1);
  const [inspectionsPage, setInspectionsPage] = useState(1);

  /* loading */
  const [loading, setLoading] = useState(true);

  /* ─── tab change ─── */
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  /* ─── fetch helpers ─── */
  const apiFetch = async (path, opts) => {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      headers: { 'Content-Type': 'application/json' },
      ...opts,
    });
    if (!res.ok) throw new Error(`API error ${res.status}`);
    return res.json();
  };

  /* ─── load properties + tenants (for dropdowns) ─── */
  useEffect(() => {
    (async () => {
      try {
        const [p, t] = await Promise.all([
          apiFetch('/api/properties'),
          apiFetch('/api/tenants'),
        ]);
        setProperties(Array.isArray(p) ? p : p.properties || []);
        setTenantsList(Array.isArray(t) ? t : t.tenants || []);
      } catch (e) { console.error('Error loading properties/tenants:', e); }
    })();
  }, []);

  /* ─── dashboard data ─── */
  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const [summary, legalData] = await Promise.all([
        apiFetch('/api/legal-dashboard-summary').catch(() => null),
        apiFetch('/api/legal-dashboard').catch(() => null),
      ]);
      if (summary) {
        setDashboardSummary(summary);
        if (summary.upcoming_deadlines) setUpcomingDeadlines(summary.upcoming_deadlines);
        if (summary.risk_assessment) setRiskAssessment(summary.risk_assessment);
      }
      if (legalData) {
        if (legalData.summary) {
          setDashboardSummary(prev => ({
            ...prev,
            active_cases: legalData.summary.openCases || prev.active_cases,
            pending_evictions: legalData.summary.pendingEvictions || prev.pending_evictions,
            upcoming_renewals: legalData.summary.upcomingExpirations || prev.upcoming_renewals,
          }));
        }
        if (legalData.tenants) {
          const cards = legalData.tenants.map(t => {
            let riskScore = 25;
            if (t.legal_status === 'eviction') riskScore = 95;
            else if (t.legal_status === 'on_notice') riskScore = 85;
            else if (t.legal_status === 'heavy_offender') riskScore = 75;
            else if (t.legal_status === 'light_offender') riskScore = 55;
            else if (t.complaint_count > 0) riskScore = 45;
            return {
              id: t.id,
              name: `${t.first_name} ${t.last_name}`,
              type: 'Individual',
              leaseStart: t.lease_start_date,
              leaseEnd: t.lease_end_date,
              property: t.property_name || t.unit_address || 'N/A',
              property_id: t.property_id,
              riskScore,
              outstandingBalance: t.outstanding_balance || 0,
              lastPayment: t.last_payment_date || '--',
              evictionStatus: t.legal_status === 'eviction' ? 'Active' : t.legal_status === 'on_notice' ? 'Notice Served' : 'None',
              legalStatus: t.legal_status,
              legalNotes: t.legal_notes,
              complaintCount: t.complaint_count,
              email: t.email,
              phone: t.phone,
              monthlyRent: t.monthly_rent,
            };
          });
          setTenantCards(cards);

          // build deadlines from tenant data
          const deadlines = [];
          let did = 1;
          legalData.tenants.forEach(t => {
            const name = `${t.first_name} ${t.last_name}`;
            if (t.legal_status === 'eviction') {
              deadlines.push({ id: did++, title: 'Court Hearing', subtitle: name, description: `Eviction hearing - ${t.unit_address || 'TBD'}`, date: '2025-02-15', urgent: true });
            }
            if (t.lease_end_date) {
              const days = Math.ceil((new Date(t.lease_end_date) - new Date()) / 86400000);
              if (days > 0 && days <= 60) {
                deadlines.push({ id: did++, title: 'Lease Expiration', subtitle: name, description: 'Lease expiring soon', date: t.lease_end_date, urgent: days <= 30 });
              }
            }
            if (t.complaint_count >= 3) {
              deadlines.push({ id: did++, title: 'Compliance Review', subtitle: name, description: `${t.complaint_count} complaints filed`, date: '2025-02-20', urgent: true });
            }
          });
          deadlines.sort((a, b) => (b.urgent ? 1 : 0) - (a.urgent ? 1 : 0) || new Date(a.date) - new Date(b.date));
          if (!summary?.upcoming_deadlines) setUpcomingDeadlines(deadlines.slice(0, 5));

          // risk counts
          let high = 0, med = 0, low = 0;
          cards.forEach(c => { if (c.riskScore >= 70) high++; else if (c.riskScore >= 40) med++; else low++; });
          if (!summary?.risk_assessment) setRiskAssessment({ high_risk: high, medium_risk: med, low_risk: low });
        }
      }
    } catch (e) { console.error('Dashboard load error:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  /* ─── legal cases ─── */
  const loadCases = useCallback(async () => {
    try {
      const data = await apiFetch('/api/legal-cases');
      setCases(Array.isArray(data) ? data : data.cases || []);
    } catch (e) { console.error('Error loading cases:', e); }
  }, []);

  /* ─── compliance ─── */
  const loadCompliance = useCallback(async () => {
    try {
      const data = await apiFetch('/api/compliance-items');
      setComplianceItems(Array.isArray(data) ? data : data.items || []);
    } catch (e) { console.error('Error loading compliance:', e); }
  }, []);

  /* ─── scores ─── */
  const loadScores = useCallback(async () => {
    try {
      const data = await apiFetch('/api/tenant-scores');
      setScores(Array.isArray(data) ? data : data.scores || []);
    } catch (e) { console.error('Error loading scores:', e); }
  }, []);

  /* ─── kanban ─── */
  const loadKanban = useCallback(async () => {
    try {
      const data = await apiFetch('/api/eviction-notices/by-stage');
      setKanbanData(data.stages || data || {});
    } catch (e) {
      console.error('Error loading kanban:', e);
      // fallback: load flat list and group
      try {
        const flat = await apiFetch('/api/eviction-notices');
        const arr = Array.isArray(flat) ? flat : flat.notices || [];
        const grouped = {};
        EVICTION_STAGES.forEach(s => { grouped[s.key] = []; });
        arr.forEach(n => { const s = n.stage || 'notice_to_cure'; if (grouped[s]) grouped[s].push(n); else grouped['notice_to_cure'].push(n); });
        setKanbanData(grouped);
      } catch (e2) { console.error('Error loading eviction notices:', e2); }
    }
  }, []);
  const loadEvictions = loadKanban;

  /* ─── renewals ─── */
  const loadRenewals = useCallback(async () => {
    try {
      const data = await apiFetch('/api/lease-renewals');
      setRenewals(Array.isArray(data) ? data : data.renewals || []);
    } catch (e) { console.error('Error loading renewals:', e); }
  }, []);

  /* ─── inspections ─── */
  const loadInspections = useCallback(async () => {
    try {
      const data = await apiFetch('/api/inspections');
      setInspections(Array.isArray(data) ? data : data.inspections || []);
    } catch (e) { console.error('Error loading inspections:', e); }
  }, []);

  /* ─── lazy-load tab data ─── */
  useEffect(() => {
    if (activeTab === 'Legal Cases' && cases.length === 0) loadCases();
    if (activeTab === 'Compliance' && complianceItems.length === 0) loadCompliance();
    if (activeTab === 'Tenant Scoring' && scores.length === 0) loadScores();
    if (activeTab === 'Notices & Evictions' && Object.keys(kanbanData).length === 0) loadKanban();
    if (activeTab === 'Lease Renewals' && renewals.length === 0) loadRenewals();
    if (activeTab === 'Inspections' && inspections.length === 0) loadInspections();
  }, [activeTab]);

  /* ─── property filter helper ─── */
  const filterByProperty = useCallback((items, propField = 'property_id') => {
    if (!propertyFilter) return items;
    return items.filter(i => String(i[propField]) === String(propertyFilter) || String(i.property_name || '').toLowerCase().includes(propertyFilter.toLowerCase()));
  }, [propertyFilter]);

  /* ═══════════════════════════════════════════════════════════════
     CRUD HANDLERS
     ═══════════════════════════════════════════════════════════════ */

  /* ─── CASES ─── */
  const openCasePanel = (item = null) => {
    setEditingCase(item);
    setCaseForm(item ? { ...item } : {
      case_number: '', tenant_id: '', property_id: '', case_type: 'eviction',
      stage: 'filing', priority: 'medium', filed_date: today(), next_hearing: '',
      amount: '', description: '', notes: ''
    });
    setCasePanel(true);
  };
  const saveCase = async () => {
    try {
      if (editingCase?.id) {
        await apiFetch(`/api/legal-cases/${editingCase.id}`, { method: 'PUT', body: JSON.stringify(caseForm) });
      } else {
        await apiFetch('/api/legal-cases', { method: 'POST', body: JSON.stringify(caseForm) });
      }
      setCasePanel(false);
      loadCases();
    } catch (e) { console.error('Error saving case:', e); }
  };
  const deleteCase = async (id) => {
    if (!window.confirm('Delete this case?')) return;
    try {
      await apiFetch(`/api/legal-cases/${id}`, { method: 'DELETE' });
      loadCases();
    } catch (e) { console.error('Error deleting case:', e); }
  };

  /* ─── EVICTION / KANBAN ─── */
  const openEvictionPanel = (item = null) => {
    setEditingEviction(item);
    setEvictionForm(item ? { ...item } : {
      tenant_id: '', property_id: '', stage: 'notice_to_cure',
      notice_type: 'cure', reason: '', notice_date: today(),
      deadline_date: '', amount_owed: '', notes: ''
    });
    setEvictionPanel(true);
  };
  const saveEviction = async () => {
    try {
      if (editingEviction?.id) {
        await apiFetch(`/api/eviction-notices/${editingEviction.id}`, { method: 'PUT', body: JSON.stringify(evictionForm) });
      } else {
        await apiFetch('/api/eviction-notices', { method: 'POST', body: JSON.stringify(evictionForm) });
      }
      setEvictionPanel(false);
      loadKanban();
    } catch (e) { console.error('Error saving eviction:', e); }
  };
  const deleteEviction = async (id) => {
    if (!window.confirm('Delete this eviction notice?')) return;
    try {
      await apiFetch(`/api/eviction-notices/${id}`, { method: 'DELETE' });
      loadKanban();
    } catch (e) { console.error('Error deleting eviction:', e); }
  };
  const handleDragStart = (e, noticeId) => {
    e.dataTransfer.setData('text/plain', String(noticeId));
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e, stageKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageKey);
  };
  const handleDragLeave = () => { setDragOverStage(null); };
  const handleDrop = async (e, newStage) => {
    e.preventDefault();
    setDragOverStage(null);
    const noticeId = e.dataTransfer.getData('text/plain');
    if (!noticeId) return;
    try {
      await apiFetch(`/api/eviction-notices/${noticeId}/stage`, {
        method: 'PUT',
        body: JSON.stringify({ stage: newStage, stage_order: 0 })
      });
      loadKanban();
    } catch (err) { console.error('Error moving notice:', err); }
  };

  /* ─── RENEWALS ─── */
  const openRenewalPanel = (item = null) => {
    setEditingRenewal(item);
    setRenewalForm(item ? { ...item } : {
      tenant_id: '', property_id: '', current_end_date: '', proposed_end_date: '',
      current_rent: '', proposed_rent: '', status: 'pending', notes: ''
    });
    setRenewalPanel(true);
  };
  const saveRenewal = async () => {
    try {
      if (editingRenewal?.id) {
        await apiFetch(`/api/lease-renewals/${editingRenewal.id}`, { method: 'PUT', body: JSON.stringify(renewalForm) });
      } else {
        await apiFetch('/api/lease-renewals', { method: 'POST', body: JSON.stringify(renewalForm) });
      }
      setRenewalPanel(false);
      loadRenewals();
    } catch (e) { console.error('Error saving renewal:', e); }
  };
  const deleteRenewal = async (id) => {
    if (!window.confirm('Delete this renewal?')) return;
    try {
      await apiFetch(`/api/lease-renewals/${id}`, { method: 'DELETE' });
      loadRenewals();
    } catch (e) { console.error('Error deleting renewal:', e); }
  };

  /* ─── INSPECTIONS ─── */
  const openInspectionPanel = (item = null) => {
    setEditingInspection(item);
    setInspectionForm(item ? { ...item } : {
      property_id: '', inspection_type: 'routine', status: 'scheduled',
      scheduled_date: today(), inspector: '', follow_up_required: false, notes: ''
    });
    setInspectionPanel(true);
  };
  const saveInspection = async () => {
    try {
      if (editingInspection?.id) {
        await apiFetch(`/api/inspections/${editingInspection.id}`, { method: 'PUT', body: JSON.stringify(inspectionForm) });
      } else {
        await apiFetch('/api/inspections', { method: 'POST', body: JSON.stringify(inspectionForm) });
      }
      setInspectionPanel(false);
      loadInspections();
    } catch (e) { console.error('Error saving inspection:', e); }
  };
  const deleteInspection = async (id) => {
    if (!window.confirm('Delete this inspection?')) return;
    try {
      await apiFetch(`/api/inspections/${id}`, { method: 'DELETE' });
      loadInspections();
    } catch (e) { console.error('Error deleting inspection:', e); }
  };

  /* ─── COMPLIANCE ─── */
  const saveComplianceItem = async (item) => {
    try {
      if (item.id) {
        await apiFetch(`/api/compliance-items/${item.id}`, { method: 'PUT', body: JSON.stringify(item) });
      } else {
        await apiFetch('/api/compliance-items', { method: 'POST', body: JSON.stringify(item) });
      }
      loadCompliance();
    } catch (e) { console.error('Error saving compliance item:', e); }
  };

  /* ═══════════════════════════════════════════════════════════════
     EVICTION HELPERS
     ═══════════════════════════════════════════════════════════════ */
  const getEvictionList = () => {
    const all = [];
    Object.entries(kanbanData).forEach(([stage, items]) => {
      items.forEach(item => all.push({ ...item, stage }));
    });
    return all;
  };

  const getDaysInStage = (notice) => {
    const updated = notice.updated_at || notice.created_at;
    if (!updated) return 0;
    const diff = Date.now() - new Date(updated).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  const getNextAction = (notice) => {
    const stage = notice.stage || 'notice_to_cure';
    const actions = {
      notice_to_cure: 'Serve notice',
      notice_to_quit: 'File UD complaint',
      pay_or_quit: 'File UD if unpaid',
      ud_filed: 'Prepare for hearing',
      court_hearing: 'Attend hearing',
      judgment: 'Request writ',
      writ_lockout: 'Schedule lockout',
      resolved: 'Case closed',
    };
    return actions[stage] || 'Review case';
  };

  const getEvStats = () => {
    const all = getEvictionList();
    const now = new Date();
    const thirtyDaysAgo = new Date(now); thirtyDaysAgo.setDate(now.getDate() - 30);
    const weekFromNow = new Date(now); weekFromNow.setDate(now.getDate() + 7);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    return {
      overdue: all.filter(n => n.cure_deadline && new Date(n.cure_deadline) < now && n.stage !== 'resolved').length,
      dueThisWeek: all.filter(n => n.cure_deadline && new Date(n.cure_deadline) >= now && new Date(n.cure_deadline) <= weekFromNow).length,
      active: all.filter(n => n.stage !== 'resolved').length,
      courtThisMonth: all.filter(n => ['court_hearing', 'ud_filed'].includes(n.stage) && n.next_hearing_date && new Date(n.next_hearing_date) <= monthEnd).length,
      resolved30d: all.filter(n => n.stage === 'resolved' && n.updated_at && new Date(n.updated_at) >= thirtyDaysAgo).length,
    };
  };

  /* ═══════════════════════════════════════════════════════════════
     CALENDAR
     ═══════════════════════════════════════════════════════════════ */
  const renderCalendar = () => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrev = new Date(year, month, 0).getDate();
    const todayObj = new Date();
    const rows = [];
    let day = 1, nextDay = 1;
    for (let week = 0; week < 6; week++) {
      const cells = [];
      for (let d = 0; d < 7; d++) {
        const idx = week * 7 + d;
        if (idx < firstDay) {
          cells.push(<div key={`p${d}`} className="cal-day other-month">{daysInPrev - firstDay + idx + 1}</div>);
        } else if (day > daysInMonth) {
          cells.push(<div key={`n${d}`} className="cal-day other-month">{nextDay++}</div>);
        } else {
          const isToday = day === todayObj.getDate() && month === todayObj.getMonth() && year === todayObj.getFullYear();
          cells.push(<div key={day} className={`cal-day${isToday ? ' today' : ''}`}>{day}</div>);
          day++;
        }
      }
      rows.push(<div key={week} className="cal-row">{cells}</div>);
      if (day > daysInMonth) break;
    }
    return (
      <div className="legal-calendar">
        <div className="cal-header">
          <button className="cal-nav" onClick={() => setCalendarDate(new Date(year, month - 1, 1))}>‹</button>
          <span className="cal-month">{calendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
          <button className="cal-nav" onClick={() => setCalendarDate(new Date(year, month + 1, 1))}>›</button>
        </div>
        <div className="cal-weekdays">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <div key={d} className="cal-weekday">{d}</div>)}
        </div>
        {rows}
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════════════════
     TAB RENDERERS
     ═══════════════════════════════════════════════════════════════ */

  /* ─── 1. DASHBOARD ─── */
  const renderDashboard = () => {
    const stats = [
      { label: 'Active Cases', value: dashboardSummary.active_cases || dashboardSummary.openCases || 0, icon: '⚖️', desc: 'Open legal matters' },
      { label: 'Pending Evictions', value: dashboardSummary.pending_evictions || dashboardSummary.pendingEvictions || 0, icon: '🚨', desc: 'In progress' },
      { label: 'Upcoming Renewals', value: dashboardSummary.upcoming_renewals || dashboardSummary.upcomingExpirations || 0, icon: '📄', desc: 'Within 90 days' },
      { label: 'Compliance Issues', value: dashboardSummary.compliance_issues || 0, icon: '⚠️', desc: 'Require attention' },
      { label: 'Overdue Inspections', value: dashboardSummary.overdue_inspections || 0, icon: '🔍', desc: 'Past due date' },
      { label: 'Total Legal Spend', value: dashboardSummary.total_legal_spend ? fmt(dashboardSummary.total_legal_spend) : '$0.00', icon: '💰', desc: 'Year to date', isFormatted: true },
    ];
    const totalRisk = (riskAssessment.high_risk || 0) + (riskAssessment.medium_risk || 0) + (riskAssessment.low_risk || 0) || 1;
    return (
      <div className="dashboard-layout">
        <div className="stats-row">
          {stats.map((s, i) => (
            <div key={i} className="stat-card">
              <div className="stat-icon">{s.icon}</div>
              <div className="stat-body">
                <div className="stat-label">{s.label}</div>
                <div className="stat-value">{s.isFormatted ? s.value : s.value}</div>
                <div className="stat-desc">{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="dashboard-grid">
          <div className="dashboard-panel">
            <div className="dpanel-header">
              <span className="dpanel-icon">📅</span>
              <h3>Upcoming Deadlines</h3>
            </div>
            {upcomingDeadlines.length === 0 ? (
              <div className="empty-placeholder">No upcoming deadlines</div>
            ) : (
              <div className="deadlines-list">
                {upcomingDeadlines.map((d, i) => (
                  <div key={d.id || i} className="deadline-row">
                    <div className="deadline-left">
                      <div className="deadline-title">{d.title}</div>
                      <div className="deadline-sub">{d.subtitle}</div>
                      <div className="deadline-desc">{d.description}</div>
                    </div>
                    <div className="deadline-right">
                      <div className="deadline-date">{fmtDate(d.date)}</div>
                      {d.urgent && <span className="urgent-badge">Urgent</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="dashboard-panel">
            <div className="dpanel-header">
              <span className="dpanel-icon">📆</span>
              <h3>Calendar</h3>
            </div>
            {renderCalendar()}
          </div>
          <div className="dashboard-panel full-width">
            <div className="dpanel-header">
              <span className="dpanel-icon">📊</span>
              <h3>Risk Assessment</h3>
            </div>
            <div className="risk-bars">
              <div className="risk-row">
                <div className="risk-lbl">High Risk</div>
                <div className="risk-bar-track"><div className="risk-bar-fill" style={{ width: `${(riskAssessment.high_risk / totalRisk) * 100}%`, background: '#EF4444' }} /></div>
                <div className="risk-val">{riskAssessment.high_risk}</div>
              </div>
              <div className="risk-row">
                <div className="risk-lbl">Medium Risk</div>
                <div className="risk-bar-track"><div className="risk-bar-fill" style={{ width: `${(riskAssessment.medium_risk / totalRisk) * 100}%`, background: '#F59E0B' }} /></div>
                <div className="risk-val">{riskAssessment.medium_risk}</div>
              </div>
              <div className="risk-row">
                <div className="risk-lbl">Low Risk</div>
                <div className="risk-bar-track"><div className="risk-bar-fill" style={{ width: `${(riskAssessment.low_risk / totalRisk) * 100}%`, background: '#10B981' }} /></div>
                <div className="risk-val">{riskAssessment.low_risk}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ─── 2. TENANT CARDS ─── */
  const renderTenantCards = () => {
    let filtered = filterByProperty(tenantCards, 'property_id');
    if (tenantCardsSearch) {
      const q = tenantCardsSearch.toLowerCase();
      filtered = filtered.filter(t => t.name.toLowerCase().includes(q) || (t.property || '').toLowerCase().includes(q));
    }
    return (
      <div className="tab-section">
        <div className="section-toolbar">
          <h2>Tenant Cards</h2>
          <input type="text" className="legal-search" placeholder="Search tenants..." value={tenantCardsSearch} onChange={e => setTenantCardsSearch(e.target.value)} />
        </div>
        {filtered.length === 0 ? (
          <div className="empty-placeholder">No tenants found</div>
        ) : (
          <div className="tenant-card-grid">
            {filtered.map(t => (
              <div key={t.id} className="tcard">
                <div className="tcard-top">
                  <div className="tcard-name">{t.name}</div>
                  <StatusBadge status={t.legalStatus || 'good_standing'} />
                </div>
                <div className="tcard-property">{t.property}</div>
                <div className="tcard-row">
                  <span className="tcard-label">Risk Score</span>
                  <span className="tcard-val" style={{ color: scoreColor(t.riskScore) }}>{t.riskScore}</span>
                </div>
                <div className="score-bar-track small">
                  <div className="score-bar-fill" style={{ width: `${t.riskScore}%`, background: scoreColor(t.riskScore) }} />
                </div>
                <div className="tcard-row">
                  <span className="tcard-label">Rent</span>
                  <span className="tcard-val">{fmt(t.monthlyRent)}</span>
                </div>
                <div className="tcard-row">
                  <span className="tcard-label">Outstanding</span>
                  <span className="tcard-val" style={{ color: t.outstandingBalance > 0 ? '#EF4444' : '#10B981' }}>{fmt(t.outstandingBalance)}</span>
                </div>
                <div className="tcard-row">
                  <span className="tcard-label">Lease End</span>
                  <span className="tcard-val">{fmtDate(t.leaseEnd)}</span>
                </div>
                <div className="tcard-row">
                  <span className="tcard-label">Eviction</span>
                  <StatusBadge status={t.evictionStatus === 'None' ? 'inactive' : 'critical'} />
                </div>
                <div className="tcard-row">
                  <span className="tcard-label">Complaints</span>
                  <span className="tcard-val">{t.complaintCount || 0}</span>
                </div>
                <div className="tcard-actions">
                  <button className="tcard-btn" onClick={() => { handleTabChange('Legal Cases'); }}>View Cases</button>
                  <button className="tcard-btn" onClick={() => { handleTabChange('Notices & Evictions'); }}>Notices</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  /* ─── 3. LEGAL CASES ─── */
  const casesSort = useSort('filed_date', 'desc');
  const renderCases = () => {
    let filtered = filterByProperty(cases);
    if (casesSearch) {
      const q = casesSearch.toLowerCase();
      filtered = filtered.filter(c =>
        (c.case_number || '').toLowerCase().includes(q) ||
        (c.tenant_name || '').toLowerCase().includes(q) ||
        (c.property_name || '').toLowerCase().includes(q) ||
        (c.case_type || '').toLowerCase().includes(q)
      );
    }
    const sorted = [...filtered].sort(casesSort.sortFn);
    const { total, paginated } = paginate(sorted, casesPage);
    const page = casesPage; const setPage = setCasesPage;
    return (
      <div className="tab-section">
        <div className="section-toolbar">
          <h2>Legal Cases</h2>
          <div className="toolbar-right">
            <input type="text" className="legal-search" placeholder="Search cases..." value={casesSearch} onChange={e => setCasesSearch(e.target.value)} />
            <button className="btn-primary" onClick={() => openCasePanel()}>+ New Case</button>
          </div>
        </div>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr>
                <SortHeader label="Case #" colKey="case_number" {...casesSort} onSort={casesSort.toggle} />
                <SortHeader label="Tenant" colKey="tenant_name" {...casesSort} onSort={casesSort.toggle} />
                <SortHeader label="Property" colKey="property_name" {...casesSort} onSort={casesSort.toggle} />
                <SortHeader label="Type" colKey="case_type" {...casesSort} onSort={casesSort.toggle} />
                <SortHeader label="Stage" colKey="stage" {...casesSort} onSort={casesSort.toggle} />
                <SortHeader label="Priority" colKey="priority" {...casesSort} onSort={casesSort.toggle} />
                <SortHeader label="Filed" colKey="filed_date" {...casesSort} onSort={casesSort.toggle} />
                <SortHeader label="Next Hearing" colKey="next_hearing" {...casesSort} onSort={casesSort.toggle} />
                <SortHeader label="Amount" colKey="amount" {...casesSort} onSort={casesSort.toggle} />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr><td colSpan="10" className="empty-row">No cases found</td></tr>
              ) : paginated.map(c => (
                <tr key={c.id}>
                  <td className="mono">{c.case_number || '--'}</td>
                  <td>{c.tenant_name || '--'}</td>
                  <td>{c.property_name || '--'}</td>
                  <td>{(c.case_type || '').replace(/_/g, ' ')}</td>
                  <td><StatusBadge status={c.stage} /></td>
                  <td><StatusBadge status={c.priority} /></td>
                  <td>{fmtDate(c.filed_date)}</td>
                  <td>{fmtDate(c.next_hearing)}</td>
                  <td>{c.amount ? fmt(c.amount) : '--'}</td>
                  <td className="action-cell">
                    <button className="icon-btn" title="Edit" onClick={() => openCasePanel(c)}>✏️</button>
                    <button className="icon-btn" title="Delete" onClick={() => deleteCase(c.id)}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} total={total} setPage={setPage} />

        {/* Side Panel */}
        {casePanel && (
          <div className="legal-side-panel-overlay" onClick={() => setCasePanel(false)}>
            <div className="legal-side-panel" onClick={e => e.stopPropagation()}>
              <div className="sp-header">
                <h3>{editingCase ? 'Edit Case' : 'New Case'}</h3>
                <button className="sp-close" onClick={() => setCasePanel(false)}>×</button>
              </div>
              <div className="sp-body">
                <label>Case Number<input type="text" value={caseForm.case_number || ''} onChange={e => setCaseForm({ ...caseForm, case_number: e.target.value })} /></label>
                <label>Tenant
                  <select value={caseForm.tenant_id || ''} onChange={e => setCaseForm({ ...caseForm, tenant_id: e.target.value })}>
                    <option value="">Select Tenant</option>
                    {tenantsList.map(t => <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>)}
                  </select>
                </label>
                <label>Property
                  <select value={caseForm.property_id || ''} onChange={e => setCaseForm({ ...caseForm, property_id: e.target.value })}>
                    <option value="">Select Property</option>
                    {properties.map(p => <option key={p.id} value={p.id}>{p.name || p.address}</option>)}
                  </select>
                </label>
                <label>Type
                  <select value={caseForm.case_type || ''} onChange={e => setCaseForm({ ...caseForm, case_type: e.target.value })}>
                    <option value="eviction">Eviction</option>
                    <option value="non_payment">Non-Payment</option>
                    <option value="lease_violation">Lease Violation</option>
                    <option value="property_damage">Property Damage</option>
                    <option value="noise_complaint">Noise Complaint</option>
                    <option value="other">Other</option>
                  </select>
                </label>
                <label>Stage
                  <select value={caseForm.stage || ''} onChange={e => setCaseForm({ ...caseForm, stage: e.target.value })}>
                    <option value="filing">Filing</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="review">Review</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </label>
                <label>Priority
                  <select value={caseForm.priority || ''} onChange={e => setCaseForm({ ...caseForm, priority: e.target.value })}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </label>
                <label>Filed Date<input type="date" value={caseForm.filed_date || ''} onChange={e => setCaseForm({ ...caseForm, filed_date: e.target.value })} /></label>
                <label>Next Hearing<input type="date" value={caseForm.next_hearing || ''} onChange={e => setCaseForm({ ...caseForm, next_hearing: e.target.value })} /></label>
                <label>Amount<input type="number" step="0.01" value={caseForm.amount || ''} onChange={e => setCaseForm({ ...caseForm, amount: e.target.value })} /></label>
                <label>Description<textarea rows="3" value={caseForm.description || ''} onChange={e => setCaseForm({ ...caseForm, description: e.target.value })} /></label>
                <label>Notes<textarea rows="3" value={caseForm.notes || ''} onChange={e => setCaseForm({ ...caseForm, notes: e.target.value })} /></label>
              </div>
              <div className="sp-footer">
                <button className="btn-primary" onClick={saveCase}>Save</button>
                <button className="btn-secondary" onClick={() => setCasePanel(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  /* ─── 4. COMPLIANCE ─── */
  const renderCompliance = () => {
    const itemsByCategory = {};
    COMPLIANCE_CATEGORIES.forEach(cat => { itemsByCategory[cat.key] = complianceItems.filter(i => i.category === cat.key); });
    return (
      <div className="tab-section">
        <div className="section-toolbar">
          <h2>Compliance Tracker</h2>
        </div>
        <div className="compliance-grid">
          {COMPLIANCE_CATEGORIES.map(cat => {
            const items = itemsByCategory[cat.key] || [];
            const compliantCount = items.filter(i => i.status === 'compliant').length;
            const totalCount = items.length;
            const overallStatus = totalCount === 0 ? 'inactive' : compliantCount === totalCount ? 'compliant' : items.some(i => i.status === 'critical') ? 'critical' : 'warning';
            return (
              <div key={cat.key} className="compliance-card">
                <div className="cc-icon">{cat.icon}</div>
                <h3>{cat.label}</h3>
                <StatusBadge status={overallStatus} />
                <p className="cc-desc">{cat.description}</p>
                {totalCount > 0 && (
                  <div className="cc-progress">
                    <div className="cc-progress-track">
                      <div className="cc-progress-fill" style={{ width: `${(compliantCount / totalCount) * 100}%` }} />
                    </div>
                    <span className="cc-progress-label">{compliantCount}/{totalCount} items compliant</span>
                  </div>
                )}
                {items.length > 0 && (
                  <div className="cc-items">
                    {items.slice(0, 3).map(item => (
                      <div key={item.id} className="cc-item">
                        <span>{item.title || item.name}</span>
                        <StatusBadge status={item.status} />
                      </div>
                    ))}
                    {items.length > 3 && <div className="cc-more">+{items.length - 3} more items</div>}
                  </div>
                )}
                {totalCount === 0 && (
                  <div className="cc-empty">No items tracked yet</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* ─── 5. DOCUMENTS (includes templates) ─── */
  const renderDocuments = () => {
    const tiers = [
      { key: 'master', label: 'Master Templates', desc: 'Organization-wide base templates', data: TEMPLATE_DATA.master },
      { key: 'property', label: 'Property-Specific Templates', desc: 'Templates customized per property', data: TEMPLATE_DATA.property },
      { key: 'tenant', label: 'Tenant-Specific Templates', desc: 'Individual tenant document templates', data: TEMPLATE_DATA.tenant },
    ];
    return (
      <div className="tab-section">
        <SuperDocDocumentManager />
        <div className="templates-section">
          <div className="section-toolbar" style={{ marginTop: 32 }}>
            <h2>Document Templates</h2>
          </div>
          {tiers.map(tier => (
            <div key={tier.key} className="template-tier">
              <div className="tier-header">
                <h3>{tier.label}</h3>
                <p>{tier.desc}</p>
              </div>
              <div className="template-card-grid">
                {tier.data.map(tmpl => (
                  <div key={tmpl.id} className="template-card">
                    <div className="tmpl-icon">📄</div>
                    <h4>{tmpl.title}</h4>
                    <p>{tmpl.description}</p>
                    <div className="tmpl-actions">
                      <button className="btn-primary small">Use Template</button>
                      <button className="btn-secondary small">Edit</button>
                      <button className="btn-secondary small">Preview</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  /* ─── 6. TENANT SCORING ─── */
  const scoresSort = useSort('overall_score', 'desc');
  const renderTenantScoring = () => {
    let filtered = scores;
    if (scoresSearch) {
      const q = scoresSearch.toLowerCase();
      filtered = filtered.filter(s =>
        (s.tenant_name || s.name || '').toLowerCase().includes(q) ||
        (s.property_name || '').toLowerCase().includes(q)
      );
    }
    const sorted = [...filtered].sort(scoresSort.sortFn);
    const { total, paginated } = paginate(sorted, scoresPage);
    const page = scoresPage; const setPage = setScoresPage;
    return (
      <div className="tab-section">
        <div className="section-toolbar">
          <h2>Tenant Scoring</h2>
          <input type="text" className="legal-search" placeholder="Search tenants..." value={scoresSearch} onChange={e => setScoresSearch(e.target.value)} />
        </div>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr>
                <SortHeader label="Tenant" colKey="tenant_name" {...scoresSort} onSort={scoresSort.toggle} />
                <SortHeader label="Property" colKey="property_name" {...scoresSort} onSort={scoresSort.toggle} />
                <SortHeader label="Score" colKey="overall_score" {...scoresSort} onSort={scoresSort.toggle} />
                <th>Score Bar</th>
                <th>Risk Level</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr><td colSpan="6" className="empty-row">No scoring data available</td></tr>
              ) : paginated.map(s => {
                const score = s.overall_score ?? s.score ?? 0;
                const riskLevel = score >= 70 ? 'low' : score >= 40 ? 'medium' : 'high';
                const tenantName = s.tenant_name || s.name || `Tenant ${s.tenant_id}`;
                const isExpanded = expandedScore === (s.tenant_id || s.id);
                return (
                  <React.Fragment key={s.tenant_id || s.id}>
                    <tr>
                      <td>{tenantName}</td>
                      <td>{s.property_name || '--'}</td>
                      <td className="mono" style={{ color: scoreColor(score), fontWeight: 600 }}>{score}</td>
                      <td>
                        <div className="score-bar-track">
                          <div className="score-bar-fill" style={{ width: `${score}%`, background: scoreColor(score) }} />
                        </div>
                      </td>
                      <td><StatusBadge status={riskLevel} /></td>
                      <td>
                        <button className="icon-btn" onClick={() => setExpandedScore(isExpanded ? null : (s.tenant_id || s.id))}>
                          {isExpanded ? '▲' : '▼'}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="expanded-row">
                        <td colSpan="6">
                          <div className="score-breakdown">
                            <h4>Score Breakdown</h4>
                            <div className="breakdown-grid">
                              {(s.factors || s.breakdown || [
                                { name: 'Payment History', score: s.payment_score ?? Math.round(score * 0.9 + Math.random() * 10), weight: 30 },
                                { name: 'Lease Compliance', score: s.compliance_score ?? Math.round(score * 0.85 + Math.random() * 15), weight: 25 },
                                { name: 'Communication', score: s.communication_score ?? Math.round(score * 0.95 + Math.random() * 5), weight: 15 },
                                { name: 'Property Care', score: s.property_care_score ?? Math.round(score * 0.8 + Math.random() * 20), weight: 15 },
                                { name: 'Tenure Length', score: s.tenure_score ?? Math.round(score * 0.7 + Math.random() * 30), weight: 15 },
                              ]).map((f, i) => (
                                <div key={i} className="breakdown-item">
                                  <div className="breakdown-label">
                                    <span>{f.name || f.factor}</span>
                                    <span className="breakdown-score">{Math.min(f.score || 0, 100)}/100</span>
                                  </div>
                                  <div className="score-bar-track small">
                                    <div className="score-bar-fill" style={{ width: `${Math.min(f.score || 0, 100)}%`, background: scoreColor(Math.min(f.score || 0, 100)) }} />
                                  </div>
                                  {f.weight && <div className="breakdown-weight">Weight: {f.weight}%</div>}
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination page={page} total={total} setPage={setPage} />
      </div>
    );
  };

  /* ─── 7. NOTICES & EVICTIONS ─── */

  const renderEvListView = () => {
    let items = getEvictionList();
    if (evFilterStage) items = items.filter(n => n.stage === evFilterStage);
    if (evFilterCaseType) items = items.filter(n => (n.notice_type || n.case_type) === evFilterCaseType);
    if (evFilterAssigned) items = items.filter(n => n.assigned_to === evFilterAssigned);
    if (evStatFilter === 'overdue') items = items.filter(n => n.cure_deadline && new Date(n.cure_deadline) < new Date() && n.stage !== 'resolved');
    if (evStatFilter === 'dueThisWeek') { const now = new Date(); const week = new Date(now); week.setDate(now.getDate() + 7); items = items.filter(n => n.cure_deadline && new Date(n.cure_deadline) >= now && new Date(n.cure_deadline) <= week); }
    if (evStatFilter === 'active') items = items.filter(n => n.stage !== 'resolved');
    if (evStatFilter === 'courtThisMonth') { const monthEnd = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0); items = items.filter(n => ['court_hearing', 'ud_filed'].includes(n.stage) && n.next_hearing_date && new Date(n.next_hearing_date) <= monthEnd); }
    if (evStatFilter === 'resolved30d') items = items.filter(n => n.stage === 'resolved');
    items.sort((a, b) => {
      let va, vb;
      if (evSortKey === 'days_in_stage') { va = getDaysInStage(a); vb = getDaysInStage(b); }
      else if (evSortKey === 'due_date') { va = a.cure_deadline || '9999'; vb = b.cure_deadline || '9999'; }
      else { va = a[evSortKey] || ''; vb = b[evSortKey] || ''; }
      if (va < vb) return evSortDir === 'asc' ? -1 : 1;
      if (va > vb) return evSortDir === 'asc' ? 1 : -1;
      return 0;
    });

    const { total, paginated: paginatedItems } = paginate(items, evPage);

    if (items.length === 0) {
      return (
        <div className="ev-empty-state">
          <div className="ev-empty-icon">⚖️</div>
          <h3>No active cases</h3>
          <p>When you create a notice, it will appear here</p>
          <button className="btn-primary" onClick={() => { setNewNoticeModal(true); setNewNoticeStep(1); }}>+ Create First Notice</button>
        </div>
      );
    }

    return (
      <>
        <table className="ev-table">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Property / Unit</th>
              <th>Case Type</th>
              <th>Current Stage</th>
              <th>Days in Stage</th>
              <th>Next Action</th>
              <th>Due Date</th>
              <th>Assigned</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {paginatedItems.map(notice => {
              const stageInfo = EVICTION_STAGES.find(s => s.key === notice.stage) || EVICTION_STAGES[0];
              const daysInStage = getDaysInStage(notice);
              const nextAction = getNextAction(notice);
              const isOverdue = notice.cure_deadline && new Date(notice.cure_deadline) < new Date() && notice.stage !== 'resolved';
              return (
                <tr key={notice.id} className="ev-table-row" onClick={() => { setEvSelectedNotice(notice); setEvPanelTab('timeline'); }}>
                  <td className="ev-cell-tenant">{notice.tenant_name || `Tenant ${notice.tenant_id}`}</td>
                  <td className="ev-cell-property">{notice.property_name || ''}{notice.unit_id ? ` \u2022 Unit ${notice.unit_id}` : ''}</td>
                  <td><span className="ev-case-type-badge">{CASE_TYPES.find(t => t.key === (notice.notice_type || notice.case_type))?.label || notice.notice_type || '--'}</span></td>
                  <td><span className="ev-stage-badge" style={{ background: stageInfo.color + '18', color: stageInfo.color, borderColor: stageInfo.color + '40' }}>{stageInfo.label}</span></td>
                  <td className="ev-cell-days">{daysInStage}d</td>
                  <td className={`ev-cell-action${isOverdue ? ' overdue' : ''}`}>{nextAction}</td>
                  <td className={isOverdue ? 'overdue' : ''}>{fmtDate(notice.cure_deadline)}</td>
                  <td>{notice.assigned_to || '--'}</td>
                  <td><button className="ev-view-btn" onClick={e => { e.stopPropagation(); setEvSelectedNotice(notice); setEvPanelTab('timeline'); }}>View Case →</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {total > 1 && (
          <div className="pagination-row">
            <button disabled={evPage <= 1} onClick={() => setEvPage(p => p - 1)}>← Prev</button>
            <span>Page {evPage} of {total}</span>
            <button disabled={evPage >= total} onClick={() => setEvPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </>
    );
  };

  const renderEvBoardView = () => {
    return (
      <div className="ev-board-scroll">
        <div className="ev-board">
          {EVICTION_STAGES.filter(s => s.key !== 'resolved').map(stage => {
            const items = kanbanData[stage.key] || [];
            return (
              <div key={stage.key} className={`ev-board-col${dragOverStage === stage.key ? ' drag-over' : ''}`}
                onDragOver={e => handleDragOver(e, stage.key)} onDragLeave={handleDragLeave} onDrop={e => handleDrop(e, stage.key)}>
                <div className="ev-board-col-header" style={{ '--col-color': stage.color }}>
                  <span className="ev-board-col-title">{stage.label}</span>
                  <span className="ev-board-col-count" style={{ background: stage.color + '20', color: stage.color }}>{items.length}</span>
                </div>
                <div className="ev-board-col-body">
                  {items.map(notice => {
                    const daysInStage = getDaysInStage(notice);
                    const isOverdue = notice.cure_deadline && new Date(notice.cure_deadline) < new Date();
                    return (
                      <div key={notice.id} className="ev-board-card" draggable onDragStart={e => handleDragStart(e, notice.id)}
                        onClick={() => { setEvSelectedNotice(notice); setEvPanelTab('timeline'); }}>
                        <div className="ev-bcard-top">
                          <span className={`ev-urgency-dot${isOverdue ? ' overdue' : ''}`}></span>
                          <span className="ev-bcard-tenant">{notice.tenant_name || `Tenant ${notice.tenant_id}`}</span>
                        </div>
                        <div className="ev-bcard-property">{notice.property_name || ''}{notice.unit_id ? ` \u2022 Unit ${notice.unit_id}` : ''}</div>
                        <div className="ev-bcard-meta">
                          <span className="ev-stage-badge small" style={{ background: stage.color + '18', color: stage.color }}>{stage.label}</span>
                          <span className="ev-bcard-days">{daysInStage}d</span>
                        </div>
                        {notice.cure_deadline && <div className={`ev-bcard-due${isOverdue ? ' overdue' : ''}`}>Due: {fmtDate(notice.cure_deadline)}</div>}
                        {notice.assigned_to && <div className="ev-bcard-assigned">{notice.assigned_to}</div>}
                      </div>
                    );
                  })}
                  {items.length === 0 && <div className="ev-board-empty">No items</div>}
                </div>
                <button className="ev-board-add" onClick={() => { setEvictionForm({ stage: stage.key }); setEditingEviction(null); setEvictionPanel(true); }}>+ Add</button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderEvCalendarView = () => {
    const year = evCalDate.getFullYear();
    const month = evCalDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const monthName = evCalDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const todayStr = new Date().toISOString().split('T')[0];

    // Build full dateStr for each day, store as string "YYYY-MM-DD"
    const toDateStr = (d) => `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

    const allNotices = getEvictionList();

    const getEventsForDateStr = (dateStr) => allNotices.flatMap(n => {
      const events = [];
      if (n.cure_deadline && n.cure_deadline.startsWith(dateStr)) events.push({ ...n, eventType: 'Cure deadline', dot: '#F59E0B' });
      if (n.next_hearing_date && n.next_hearing_date.startsWith(dateStr)) events.push({ ...n, eventType: 'Court hearing', dot: '#EF4444' });
      if (n.possession_date && n.possession_date.startsWith(dateStr)) events.push({ ...n, eventType: 'Filing deadline', dot: '#F97316' });
      return events;
    });

    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);

    const selectedEvents = evCalSelectedDate ? getEventsForDateStr(evCalSelectedDate) : [];
    const selectedLabel = evCalSelectedDate
      ? new Date(evCalSelectedDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      : null;

    return (
      <div className="ev-calendar">
        <div className="ev-cal-header">
          <button className="btn-secondary small" onClick={() => { setEvCalDate(new Date(year, month - 1, 1)); setEvCalSelectedDate(null); }}>← Prev</button>
          <h3 className="ev-cal-month">{monthName}</h3>
          <button className="btn-secondary small" onClick={() => { setEvCalDate(new Date(year, month + 1, 1)); setEvCalSelectedDate(null); }}>Next →</button>
        </div>
        <div className="ev-cal-legend">
          <span><span className="ev-dot" style={{ background: '#EF4444' }}></span> Court</span>
          <span><span className="ev-dot" style={{ background: '#F59E0B' }}></span> Deadlines</span>
          <span><span className="ev-dot" style={{ background: '#F97316' }}></span> Filing</span>
        </div>
        <div className="ev-cal-grid">
          {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => <div key={d} className="ev-cal-day-header">{d}</div>)}
          {days.map((d, i) => {
            const dateStr = d ? toDateStr(d) : null;
            const dots = dateStr ? [...new Set(getEventsForDateStr(dateStr).map(e => e.dot))] : [];
            const isToday = dateStr === todayStr;
            const isSelected = dateStr === evCalSelectedDate;
            return (
              <div
                key={i}
                className={`ev-cal-day${!d ? ' empty' : ''}${isToday ? ' today' : ''}${isSelected ? ' selected' : ''}`}
                onClick={() => { if (d) setEvCalSelectedDate(isSelected ? null : dateStr); }}
              >
                {d && <>
                  <span className="ev-cal-day-num">{d}</span>
                  {dots.length > 0 && <div className="ev-cal-dots">{dots.map((c, j) => <span key={j} className="ev-dot" style={{ background: c }}></span>)}</div>}
                </>}
              </div>
            );
          })}
        </div>

        {evCalSelectedDate && (
          <div className="ev-cal-events">
            <h4>{selectedLabel}</h4>
            {selectedEvents.length === 0
              ? <p className="ev-cal-no-events">No proceedings on this date</p>
              : <div className="ev-cal-event-list">
                  {selectedEvents.map((ev, idx) => {
                    const stageInfo = EVICTION_STAGES.find(s => s.key === ev.stage) || EVICTION_STAGES[0];
                    return (
                      <div key={idx} className="ev-cal-event-item" onClick={() => { setEvSelectedNotice(ev); setEvPanelTab('timeline'); }}>
                        <span className="ev-dot" style={{ background: ev.dot }}></span>
                        <div className="ev-cal-event-main">
                          <span className="ev-cal-event-tenant">{ev.tenant_name || `Tenant ${ev.tenant_id}`}</span>
                          <span className="ev-cal-event-desc">{ev.eventType} · {stageInfo.label}</span>
                        </div>
                        <span className="ev-cal-event-arrow">→</span>
                      </div>
                    );
                  })}
                </div>
            }
          </div>
        )}
      </div>
    );
  };

  const renderEvSidePanel = () => {
    if (!evSelectedNotice) return null;
    const notice = evSelectedNotice;
    const stageInfo = EVICTION_STAGES.find(s => s.key === notice.stage) || EVICTION_STAGES[0];
    const stageIdx = EVICTION_STAGES.findIndex(s => s.key === notice.stage);
    const nextStage = stageIdx < EVICTION_STAGES.length - 1 ? EVICTION_STAGES[stageIdx + 1] : null;
    const timeline = (() => { try { return JSON.parse(notice.timeline || '[]'); } catch { return []; } })();
    const notes = (() => { try { return JSON.parse(notice.notes || '[]'); } catch { return []; } })();
    const showCourt = ['ud_filed', 'court_hearing', 'judgment', 'writ_lockout'].includes(notice.stage);

    const handleAdvanceStage = async () => {
      if (!nextStage) return;
      try {
        await apiFetch(`/api/eviction-notices/${notice.id}/stage`, {
          method: 'PUT', body: JSON.stringify({ stage: nextStage.key })
        });
        loadEvictions();
        setEvSelectedNotice({ ...notice, stage: nextStage.key });
      } catch(e) { console.error(e); }
    };

    const handleAddNote = async () => {
      if (!evNewNote.trim()) return;
      const updatedNotes = [...notes, { text: evNewNote, date: new Date().toISOString(), author: 'Admin' }];
      try {
        await apiFetch(`/api/eviction-notices/${notice.id}`, {
          method: 'PUT', body: JSON.stringify({ notes: JSON.stringify(updatedNotes) })
        });
        setEvSelectedNotice({ ...notice, notes: JSON.stringify(updatedNotes) });
        setEvNewNote('');
      } catch(e) { console.error(e); }
    };

    const handleLogAction = async () => {
      if (!evNewActionType) return;
      const entry = { type: evNewActionType, notes: evNewActionNotes, date: evNewActionDate || new Date().toISOString().split('T')[0], author: 'Admin', timestamp: new Date().toISOString() };
      const updatedTimeline = [entry, ...timeline];
      try {
        await apiFetch(`/api/eviction-notices/${notice.id}`, {
          method: 'PUT', body: JSON.stringify({ timeline: JSON.stringify(updatedTimeline) })
        });
        setEvSelectedNotice({ ...notice, timeline: JSON.stringify(updatedTimeline) });
        setEvNewActionType(''); setEvNewActionNotes(''); setEvNewActionDate('');
      } catch(e) { console.error(e); }
    };

    return (
      <div className="ev-panel-overlay" onClick={() => setEvSelectedNotice(null)}>
        <div className="ev-panel" onClick={e => e.stopPropagation()}>
          <div className="ev-panel-header">
            <div className="ev-panel-header-top">
              <button className="ev-panel-back" onClick={() => setEvSelectedNotice(null)}>← All Cases</button>
              <button className="ev-panel-more">⋯</button>
            </div>
            <h3 className="ev-panel-title">{notice.tenant_name || `Tenant ${notice.tenant_id}`}</h3>
            <p className="ev-panel-subtitle">{notice.property_name || ''}{notice.unit_id ? ` \u2022 Unit ${notice.unit_id}` : ''}</p>
            <div className="ev-panel-badges">
              <span className="ev-case-type-badge">{CASE_TYPES.find(t => t.key === (notice.notice_type || notice.case_type))?.label || notice.notice_type || 'N/A'}</span>
              <span className="ev-stage-badge" style={{ background: stageInfo.color + '18', color: stageInfo.color, borderColor: stageInfo.color + '40' }}>{stageInfo.label}</span>
            </div>
          </div>

          <div className="ev-panel-tabs">
            {['timeline', 'documents', ...(showCourt ? ['court'] : []), 'notes'].map(tab => (
              <button key={tab} className={`ev-panel-tab${evPanelTab === tab ? ' active' : ''}`} onClick={() => setEvPanelTab(tab)}>
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          <div className="ev-panel-body">
            {evPanelTab === 'timeline' && (
              <div className="ev-timeline">
                <div className="ev-timeline-add">
                  <h4>Log Action</h4>
                  <select value={evNewActionType} onChange={e => setEvNewActionType(e.target.value)}>
                    <option value="">Select action type...</option>
                    <option value="notice_served">Notice Served</option>
                    <option value="notice_generated">Notice Generated</option>
                    <option value="payment_received">Payment Received</option>
                    <option value="filing_submitted">Filing Submitted</option>
                    <option value="hearing_attended">Hearing Attended</option>
                    <option value="judgment_entered">Judgment Entered</option>
                    <option value="other">Other</option>
                  </select>
                  <textarea placeholder="Notes..." rows={2} value={evNewActionNotes} onChange={e => setEvNewActionNotes(e.target.value)} />
                  <input type="date" value={evNewActionDate} onChange={e => setEvNewActionDate(e.target.value)} />
                  <button className="btn-primary small" onClick={handleLogAction} disabled={!evNewActionType}>+ Log Action</button>
                </div>
                <div className="ev-timeline-list">
                  {timeline.length === 0 && <p className="ev-timeline-empty">No timeline entries yet. Log an action above.</p>}
                  {timeline.map((entry, i) => (
                    <div key={i} className="ev-timeline-entry">
                      <div className="ev-timeline-dot"></div>
                      <div className="ev-timeline-content">
                        <div className="ev-timeline-date">{fmtDate(entry.date)}{entry.author ? ` — ${entry.author}` : ''}</div>
                        <div className="ev-timeline-text">{entry.type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}{entry.notes ? `: ${entry.notes}` : ''}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {evPanelTab === 'documents' && (
              <div className="ev-documents">
                <p className="ev-docs-placeholder">No documents attached to this case yet.</p>
                <div className="ev-docs-actions">
                  <button className="btn-primary small">+ Attach Document</button>
                  <button className="btn-secondary small">Generate from Template</button>
                </div>
              </div>
            )}

            {evPanelTab === 'court' && showCourt && (
              <div className="ev-court">
                <label>Court Case Number<input type="text" value={notice.court_case_number || ''} readOnly /></label>
                <label>Court / Jurisdiction<input type="text" value={notice.court_name || ''} readOnly /></label>
                <label>Judge<input type="text" value={notice.court_judge || ''} readOnly /></label>
                <h4>Hearing Dates</h4>
                {(() => { try { const d = JSON.parse(notice.court_dates || '[]'); return d.length ? d.map((h, i) => <div key={i} className="ev-hearing-item">{fmtDate(h.date)} — {h.type || 'Hearing'}{h.outcome ? ` (${h.outcome})` : ''}</div>) : <p>No hearing dates recorded.</p>; } catch { return <p>No hearing dates recorded.</p>; } })()}
                <button className="btn-secondary small" style={{ marginTop: 12 }}>+ Add Hearing Date</button>
                {notice.judgment_amount && <div className="ev-judgment-info"><strong>Judgment:</strong> {fmt(notice.judgment_amount)}{notice.possession_date ? ` \u2022 Possession: ${fmtDate(notice.possession_date)}` : ''}</div>}
              </div>
            )}

            {evPanelTab === 'notes' && (
              <div className="ev-notes">
                <div className="ev-notes-add">
                  <textarea placeholder="Add a note..." rows={3} value={evNewNote} onChange={e => setEvNewNote(e.target.value)} />
                  <button className="btn-primary small" onClick={handleAddNote} disabled={!evNewNote.trim()}>Add Note</button>
                </div>
                <div className="ev-notes-list">
                  {notes.length === 0 && <p className="ev-notes-empty">No notes yet.</p>}
                  {notes.map((note, i) => (
                    <div key={i} className="ev-note-item">
                      <div className="ev-note-meta">{fmtDate(note.date)}{note.author ? ` — ${note.author}` : ''}</div>
                      <div className="ev-note-text">{note.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="ev-panel-footer">
            <div className="ev-panel-footer-stage">
              <span>Current stage:</span>
              <select value={notice.stage} onChange={async (e) => {
                try {
                  await apiFetch(`/api/eviction-notices/${notice.id}/stage`, { method: 'PUT', body: JSON.stringify({ stage: e.target.value }) });
                  loadEvictions();
                  setEvSelectedNotice({ ...notice, stage: e.target.value });
                } catch(err) { console.error(err); }
              }}>
                {EVICTION_STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            {nextStage && <button className="btn-primary" onClick={handleAdvanceStage}>Advance to {nextStage.label} →</button>}
          </div>
        </div>
      </div>
    );
  };

  const renderNewNoticeModal = () => {
    if (!newNoticeModal) return null;

    const filteredTenants = tenantsList.filter(t => {
      if (!tenantSearch) return true;
      const q = tenantSearch.toLowerCase();
      return `${t.first_name} ${t.last_name}`.toLowerCase().includes(q) || (t.unit || '').toLowerCase().includes(q);
    });

    const selectedTenant = tenantsList.find(t => String(t.id) === String(newNoticeData.tenant_id));
    const selectedProperty = properties.find(p => String(p.id) === String(newNoticeData.property_id));

    return (
      <div className="ev-modal-overlay" onClick={() => setNewNoticeModal(false)}>
        <div className="ev-modal" onClick={e => e.stopPropagation()}>
          <div className="ev-modal-header">
            <h3>New Notice — Step {newNoticeStep} of 4</h3>
            <button className="sp-close" onClick={() => setNewNoticeModal(false)}>×</button>
          </div>
          <div className="ev-modal-progress">
            {[1,2,3,4].map(s => <div key={s} className={`ev-modal-step${s <= newNoticeStep ? ' active' : ''}${s === newNoticeStep ? ' current' : ''}`}>{s}</div>)}
          </div>
          <div className="ev-modal-body">
            {newNoticeStep === 1 && (
              <div className="ev-modal-step-content">
                <h4>Select Tenant</h4>
                <input type="text" placeholder="Search by tenant name or unit..." className="ev-tenant-search" value={tenantSearch} onChange={e => setTenantSearch(e.target.value)} />
                <div className="ev-tenant-list">
                  {filteredTenants.slice(0, 10).map(t => (
                    <div key={t.id} className={`ev-tenant-option${String(newNoticeData.tenant_id) === String(t.id) ? ' selected' : ''}`}
                      onClick={() => setNewNoticeData({ ...newNoticeData, tenant_id: t.id, property_id: t.property_id || '' })}>
                      <div className="ev-tenant-opt-name">{t.first_name} {t.last_name}</div>
                      <div className="ev-tenant-opt-detail">{t.unit || ''}{t.property_name ? ` \u2022 ${t.property_name}` : ''}</div>
                    </div>
                  ))}
                  {filteredTenants.length === 0 && <p className="ev-no-results">No tenants found</p>}
                </div>
              </div>
            )}

            {newNoticeStep === 2 && (
              <div className="ev-modal-step-content">
                <h4>Violation Details</h4>
                <div className="ev-case-type-select">
                  {CASE_TYPES.map(ct => (
                    <button key={ct.key} className={`ev-case-type-btn${newNoticeData.case_type === ct.key ? ' active' : ''}`}
                      onClick={() => setNewNoticeData({ ...newNoticeData, case_type: ct.key })}>{ct.label}</button>
                  ))}
                </div>
                {newNoticeData.case_type === 'non_payment' && (
                  <div className="ev-violation-fields">
                    <label>Amount Owed<input type="number" step="0.01" value={newNoticeData.amount_owed} onChange={e => setNewNoticeData({ ...newNoticeData, amount_owed: e.target.value })} placeholder="$0.00" /></label>
                    <label>Months Overdue<input type="number" value={newNoticeData.months_overdue} onChange={e => setNewNoticeData({ ...newNoticeData, months_overdue: e.target.value })} /></label>
                    <label>Last Payment Date<input type="date" value={newNoticeData.last_payment_date} onChange={e => setNewNoticeData({ ...newNoticeData, last_payment_date: e.target.value })} /></label>
                  </div>
                )}
                {newNoticeData.case_type === 'lease_violation' && (
                  <div className="ev-violation-fields">
                    <label>Violation Type
                      <select value={newNoticeData.violation_type} onChange={e => setNewNoticeData({ ...newNoticeData, violation_type: e.target.value })}>
                        <option value="">Select...</option>
                        {VIOLATION_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </label>
                    <label>Description<textarea rows={3} value={newNoticeData.violation_description} onChange={e => setNewNoticeData({ ...newNoticeData, violation_description: e.target.value })} /></label>
                    <label className="ev-checkbox-label">
                      <input type="checkbox" checked={newNoticeData.prior_warnings} onChange={e => setNewNoticeData({ ...newNoticeData, prior_warnings: e.target.checked })} />
                      Prior warnings issued
                    </label>
                    {newNoticeData.prior_warnings && <label>Number of prior warnings<input type="number" value={newNoticeData.warning_count} onChange={e => setNewNoticeData({ ...newNoticeData, warning_count: e.target.value })} /></label>}
                  </div>
                )}
                {!['non_payment', 'lease_violation'].includes(newNoticeData.case_type) && (
                  <div className="ev-violation-fields">
                    <label>Description<textarea rows={3} value={newNoticeData.reason || ''} onChange={e => setNewNoticeData({ ...newNoticeData, reason: e.target.value })} placeholder="Describe the violation..." /></label>
                  </div>
                )}
                <label>Cure Period (days)<input type="number" value={newNoticeData.cure_period} onChange={e => setNewNoticeData({ ...newNoticeData, cure_period: e.target.value })} /></label>
              </div>
            )}

            {newNoticeStep === 3 && (
              <div className="ev-modal-step-content">
                <h4>Document Preview</h4>
                <div className="ev-doc-preview">
                  <div className="ev-doc-preview-header">NOTICE TO CURE OR QUIT</div>
                  <div className="ev-doc-preview-body">
                    <p>To: <strong>{selectedTenant ? `${selectedTenant.first_name} ${selectedTenant.last_name}` : 'Tenant'}</strong></p>
                    <p>Property: <strong>{selectedProperty ? (selectedProperty.name || selectedProperty.address) : 'Property'}</strong></p>
                    <p>Date: <strong>{new Date().toLocaleDateString()}</strong></p>
                    <p>You are hereby notified that you are in violation of your lease agreement.</p>
                    {newNoticeData.case_type === 'non_payment' && <p>Amount owed: <strong>{fmt(newNoticeData.amount_owed)}</strong></p>}
                    {newNoticeData.case_type === 'lease_violation' && <p>Violation: <strong>{newNoticeData.violation_type || newNoticeData.violation_description}</strong></p>}
                    {newNoticeData.reason && <p>Details: {newNoticeData.reason}</p>}
                    <p>You have <strong>{newNoticeData.cure_period} days</strong> from the date of service to cure this violation.</p>
                  </div>
                </div>
                <p className="ev-doc-note">This is a preview. The final document will be generated with your organization's template.</p>
              </div>
            )}

            {newNoticeStep === 4 && (
              <div className="ev-modal-step-content">
                <h4>Confirm & Serve</h4>
                <label>Serving Method
                  <div className="ev-serve-methods">
                    {SERVING_METHODS.map(m => (
                      <button key={m.key} className={`ev-serve-btn${newNoticeData.serving_method === m.key ? ' active' : ''}`}
                        onClick={() => setNewNoticeData({ ...newNoticeData, serving_method: m.key })}>{m.label}</button>
                    ))}
                  </div>
                </label>
                <label>Assigned To
                  <select value={newNoticeData.assigned_to} onChange={e => setNewNoticeData({ ...newNoticeData, assigned_to: e.target.value })}>
                    <option value="">Select staff member...</option>
                  </select>
                </label>
                <div className="ev-confirm-summary">
                  <h5>Summary</h5>
                  <div className="ev-summary-row"><span>Tenant:</span><span>{selectedTenant ? `${selectedTenant.first_name} ${selectedTenant.last_name}` : '--'}</span></div>
                  <div className="ev-summary-row"><span>Case Type:</span><span>{CASE_TYPES.find(c => c.key === newNoticeData.case_type)?.label || '--'}</span></div>
                  <div className="ev-summary-row"><span>Cure Period:</span><span>{newNoticeData.cure_period} days</span></div>
                  <div className="ev-summary-row"><span>Serving Method:</span><span>{SERVING_METHODS.find(m => m.key === newNoticeData.serving_method)?.label || '--'}</span></div>
                </div>
              </div>
            )}
          </div>
          <div className="ev-modal-footer">
            {newNoticeStep > 1 && <button className="btn-secondary" onClick={() => setNewNoticeStep(s => s - 1)}>← Back</button>}
            <div style={{ flex: 1 }}></div>
            {newNoticeStep < 4 ? (
              <button className="btn-primary" onClick={() => setNewNoticeStep(s => s + 1)} disabled={newNoticeStep === 1 && !newNoticeData.tenant_id}>Next →</button>
            ) : (
              <button className="btn-primary" onClick={async () => {
                const cureDeadline = new Date(); cureDeadline.setDate(cureDeadline.getDate() + parseInt(newNoticeData.cure_period || 14));
                const body = {
                  tenant_id: newNoticeData.tenant_id,
                  property_id: newNoticeData.property_id,
                  notice_type: newNoticeData.case_type,
                  stage: 'notice_to_cure',
                  amount_owed: parseFloat(newNoticeData.amount_owed) || 0,
                  reason: newNoticeData.reason || newNoticeData.violation_description || '',
                  assigned_to: newNoticeData.assigned_to,
                  cure_deadline: cureDeadline.toISOString().split('T')[0],
                  issue_date: new Date().toISOString().split('T')[0],
                  violation_type: newNoticeData.violation_type || '',
                  timeline: JSON.stringify([{ type: 'case_opened', notes: `${CASE_TYPES.find(c => c.key === newNoticeData.case_type)?.label} case opened`, date: new Date().toISOString().split('T')[0], author: 'Admin', timestamp: new Date().toISOString() }]),
                };
                try {
                  await apiFetch('/api/eviction-notices', { method: 'POST', body: JSON.stringify(body) });
                  setNewNoticeModal(false);
                  setNewNoticeData({ tenant_id: '', property_id: '', case_type: 'non_payment', amount_owed: '', months_overdue: '', last_payment_date: '', violation_type: '', violation_description: '', prior_warnings: false, warning_count: 0, cure_period: 14, serving_method: 'all_three', assigned_to: '', reason: '', notes: '' });
                  setNewNoticeStep(1);
                  loadEvictions();
                } catch(e) { console.error(e); }
              }}>Create Case & Generate Notice</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderNoticesEvictions = () => {
    const stats = getEvStats();
    const statPills = [
      { key: 'overdue', icon: '\uD83D\uDD34', label: 'Overdue Actions', count: stats.overdue },
      { key: 'dueThisWeek', icon: '\uD83D\uDFE1', label: 'Due This Week', count: stats.dueThisWeek },
      { key: 'active', icon: '\uD83D\uDCCB', label: 'Active Cases', count: stats.active },
      { key: 'courtThisMonth', icon: '\u2696\uFE0F', label: 'Court Dates This Month', count: stats.courtThisMonth },
      { key: 'resolved30d', icon: '\u2705', label: 'Resolved (30d)', count: stats.resolved30d },
    ];

    return (
      <div className="tab-section ev-section">
        {/* Stat Pills */}
        <div className="ev-stat-pills">
          {statPills.map(pill => (
            <button key={pill.key} className={`ev-stat-pill${evStatFilter === pill.key ? ' active' : ''}`}
              onClick={() => setEvStatFilter(evStatFilter === pill.key ? '' : pill.key)}>
              <span className="ev-pill-icon">{pill.icon}</span>
              <span className="ev-pill-label">{pill.label}:</span>
              <span className="ev-pill-count">{pill.count}</span>
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div className="ev-toolbar">
          <div className="ev-view-toggle">
            <button className={`ev-toggle-btn${evView === 'list' ? ' active' : ''}`} onClick={() => setEvView('list')}>List</button>
            <button className={`ev-toggle-btn${evView === 'board' ? ' active' : ''}`} onClick={() => setEvView('board')}>Board</button>
            <button className={`ev-toggle-btn${evView === 'calendar' ? ' active' : ''}`} onClick={() => setEvView('calendar')}>Calendar</button>
          </div>
          <div className="ev-toolbar-right">
            <div className="ev-dropdown-wrap">
              <button className="btn-secondary" onClick={() => { setEvFilterOpen(!evFilterOpen); setEvSortOpen(false); }}>Filter ▾</button>
              {evFilterOpen && (
                <div className="ev-dropdown-menu">
                  <label>Stage<select value={evFilterStage} onChange={e => setEvFilterStage(e.target.value)}><option value="">All Stages</option>{EVICTION_STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}</select></label>
                  <label>Case Type<select value={evFilterCaseType} onChange={e => setEvFilterCaseType(e.target.value)}><option value="">All Types</option>{CASE_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}</select></label>
                  <button className="btn-secondary small" onClick={() => { setEvFilterStage(''); setEvFilterCaseType(''); setEvFilterAssigned(''); setEvStatFilter(''); }}>Clear Filters</button>
                </div>
              )}
            </div>
            <div className="ev-dropdown-wrap">
              <button className="btn-secondary" onClick={() => { setEvSortOpen(!evSortOpen); setEvFilterOpen(false); }}>Sort ▾</button>
              {evSortOpen && (
                <div className="ev-dropdown-menu">
                  {[{k:'due_date',l:'Due Date'},{k:'tenant_name',l:'Tenant'},{k:'stage',l:'Stage'},{k:'days_in_stage',l:'Days in Stage'},{k:'created_at',l:'Date Created'}].map(s => (
                    <button key={s.k} className={`ev-sort-opt${evSortKey === s.k ? ' active' : ''}`} onClick={() => { setEvSortKey(s.k); setEvSortDir(evSortKey === s.k && evSortDir === 'asc' ? 'desc' : 'asc'); setEvSortOpen(false); }}>{s.l} {evSortKey === s.k ? (evSortDir === 'asc' ? '↑' : '↓') : ''}</button>
                  ))}
                </div>
              )}
            </div>
            <button className="btn-primary" onClick={() => { setNewNoticeModal(true); setNewNoticeStep(1); }}>+ New Notice</button>
          </div>
        </div>

        {/* View Content */}
        {evView === 'list' && renderEvListView()}
        {evView === 'board' && renderEvBoardView()}
        {evView === 'calendar' && renderEvCalendarView()}

        {/* Side Panel */}
        {renderEvSidePanel()}

        {/* New Notice Modal */}
        {renderNewNoticeModal()}

        {/* Old eviction side panel for board "+ Add" button */}
        {evictionPanel && (
          <div className="legal-side-panel-overlay" onClick={() => setEvictionPanel(false)}>
            <div className="legal-side-panel" onClick={e => e.stopPropagation()}>
              <div className="sp-header">
                <h3>{editingEviction ? 'Edit Notice' : 'New Notice'}</h3>
                <button className="sp-close" onClick={() => setEvictionPanel(false)}>×</button>
              </div>
              <div className="sp-body">
                <label>Tenant
                  <select value={evictionForm.tenant_id || ''} onChange={e => setEvictionForm({ ...evictionForm, tenant_id: e.target.value })}>
                    <option value="">Select Tenant</option>
                    {tenantsList.map(t => <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>)}
                  </select>
                </label>
                <label>Property
                  <select value={evictionForm.property_id || ''} onChange={e => setEvictionForm({ ...evictionForm, property_id: e.target.value })}>
                    <option value="">Select Property</option>
                    {properties.map(p => <option key={p.id} value={p.id}>{p.name || p.address}</option>)}
                  </select>
                </label>
                <label>Stage
                  <select value={evictionForm.stage || 'notice_to_cure'} onChange={e => setEvictionForm({ ...evictionForm, stage: e.target.value })}>
                    {EVICTION_STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </label>
                <label>Notice Type
                  <select value={evictionForm.notice_type || 'non_payment'} onChange={e => setEvictionForm({ ...evictionForm, notice_type: e.target.value })}>
                    {CASE_TYPES.map(ct => <option key={ct.key} value={ct.key}>{ct.label}</option>)}
                  </select>
                </label>
                <label>Reason<textarea rows="3" value={evictionForm.reason || ''} onChange={e => setEvictionForm({ ...evictionForm, reason: e.target.value })} /></label>
                <label>Notice Date<input type="date" value={evictionForm.notice_date || ''} onChange={e => setEvictionForm({ ...evictionForm, notice_date: e.target.value })} /></label>
                <label>Deadline Date<input type="date" value={evictionForm.deadline_date || ''} onChange={e => setEvictionForm({ ...evictionForm, deadline_date: e.target.value })} /></label>
                <label>Amount Owed<input type="number" step="0.01" value={evictionForm.amount_owed || ''} onChange={e => setEvictionForm({ ...evictionForm, amount_owed: e.target.value })} /></label>
                <label>Notes<textarea rows="3" value={evictionForm.notes || ''} onChange={e => setEvictionForm({ ...evictionForm, notes: e.target.value })} /></label>
              </div>
              <div className="sp-footer">
                <button className="btn-primary" onClick={saveEviction}>Save</button>
                <button className="btn-secondary" onClick={() => setEvictionPanel(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  /* ─── 8. LEASE RENEWALS ─── */
  const renewalSort = useSort('current_end_date', 'asc');
  const renderRenewals = () => {
    let filtered = filterByProperty(renewals);
    if (renewalsSearch) {
      const q = renewalsSearch.toLowerCase();
      filtered = filtered.filter(r =>
        (r.tenant_name || '').toLowerCase().includes(q) ||
        (r.property_name || '').toLowerCase().includes(q)
      );
    }
    const sorted = [...filtered].sort(renewalSort.sortFn);
    const { total, paginated } = paginate(sorted, renewalsPage);
    const page = renewalsPage; const setPage = setRenewalsPage;
    return (
      <div className="tab-section">
        <div className="section-toolbar">
          <h2>Lease Renewals</h2>
          <div className="toolbar-right">
            <input type="text" className="legal-search" placeholder="Search renewals..." value={renewalsSearch} onChange={e => setRenewalsSearch(e.target.value)} />
            <button className="btn-primary" onClick={() => openRenewalPanel()}>+ New Renewal</button>
          </div>
        </div>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr>
                <SortHeader label="Tenant" colKey="tenant_name" {...renewalSort} onSort={renewalSort.toggle} />
                <SortHeader label="Property" colKey="property_name" {...renewalSort} onSort={renewalSort.toggle} />
                <SortHeader label="Current End" colKey="current_end_date" {...renewalSort} onSort={renewalSort.toggle} />
                <SortHeader label="Proposed End" colKey="proposed_end_date" {...renewalSort} onSort={renewalSort.toggle} />
                <SortHeader label="Current Rent" colKey="current_rent" {...renewalSort} onSort={renewalSort.toggle} />
                <SortHeader label="Proposed Rent" colKey="proposed_rent" {...renewalSort} onSort={renewalSort.toggle} />
                <th>Increase %</th>
                <SortHeader label="Status" colKey="status" {...renewalSort} onSort={renewalSort.toggle} />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr><td colSpan="9" className="empty-row">No renewals found</td></tr>
              ) : paginated.map(r => {
                const increase = r.current_rent && r.proposed_rent ? (((r.proposed_rent - r.current_rent) / r.current_rent) * 100).toFixed(1) : '--';
                return (
                  <tr key={r.id}>
                    <td>{r.tenant_name || '--'}</td>
                    <td>{r.property_name || '--'}</td>
                    <td>{fmtDate(r.current_end_date)}</td>
                    <td>{fmtDate(r.proposed_end_date)}</td>
                    <td>{fmt(r.current_rent)}</td>
                    <td>{fmt(r.proposed_rent)}</td>
                    <td className="mono">{increase !== '--' ? `${increase}%` : '--'}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td className="action-cell">
                      <button className="icon-btn" title="Edit" onClick={() => openRenewalPanel(r)}>✏️</button>
                      <button className="icon-btn" title="Delete" onClick={() => deleteRenewal(r.id)}>🗑️</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination page={page} total={total} setPage={setPage} />

        {/* Side Panel */}
        {renewalPanel && (
          <div className="legal-side-panel-overlay" onClick={() => setRenewalPanel(false)}>
            <div className="legal-side-panel" onClick={e => e.stopPropagation()}>
              <div className="sp-header">
                <h3>{editingRenewal ? 'Edit Renewal' : 'New Renewal'}</h3>
                <button className="sp-close" onClick={() => setRenewalPanel(false)}>×</button>
              </div>
              <div className="sp-body">
                <label>Tenant
                  <select value={renewalForm.tenant_id || ''} onChange={e => setRenewalForm({ ...renewalForm, tenant_id: e.target.value })}>
                    <option value="">Select Tenant</option>
                    {tenantsList.map(t => <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>)}
                  </select>
                </label>
                <label>Property
                  <select value={renewalForm.property_id || ''} onChange={e => setRenewalForm({ ...renewalForm, property_id: e.target.value })}>
                    <option value="">Select Property</option>
                    {properties.map(p => <option key={p.id} value={p.id}>{p.name || p.address}</option>)}
                  </select>
                </label>
                <label>Current End Date<input type="date" value={renewalForm.current_end_date || ''} onChange={e => setRenewalForm({ ...renewalForm, current_end_date: e.target.value })} /></label>
                <label>Proposed End Date<input type="date" value={renewalForm.proposed_end_date || ''} onChange={e => setRenewalForm({ ...renewalForm, proposed_end_date: e.target.value })} /></label>
                <label>Current Rent<input type="number" step="0.01" value={renewalForm.current_rent || ''} onChange={e => setRenewalForm({ ...renewalForm, current_rent: e.target.value })} /></label>
                <label>Proposed Rent<input type="number" step="0.01" value={renewalForm.proposed_rent || ''} onChange={e => setRenewalForm({ ...renewalForm, proposed_rent: e.target.value })} /></label>
                <label>Status
                  <select value={renewalForm.status || 'pending'} onChange={e => setRenewalForm({ ...renewalForm, status: e.target.value })}>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="approved">Approved</option>
                    <option value="renewed">Renewed</option>
                    <option value="rejected">Rejected</option>
                    <option value="expired">Expired</option>
                  </select>
                </label>
                <label>Notes<textarea rows="3" value={renewalForm.notes || ''} onChange={e => setRenewalForm({ ...renewalForm, notes: e.target.value })} /></label>
              </div>
              <div className="sp-footer">
                <button className="btn-primary" onClick={saveRenewal}>Save</button>
                <button className="btn-secondary" onClick={() => setRenewalPanel(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  /* ─── 9. INSPECTIONS ─── */
  const inspSort = useSort('scheduled_date', 'asc');
  const renderInspections = () => {
    let filtered = filterByProperty(inspections);
    if (inspectionsSearch) {
      const q = inspectionsSearch.toLowerCase();
      filtered = filtered.filter(i =>
        (i.property_name || '').toLowerCase().includes(q) ||
        (i.inspector || '').toLowerCase().includes(q) ||
        (i.inspection_type || '').toLowerCase().includes(q)
      );
    }
    const sorted = [...filtered].sort(inspSort.sortFn);
    const { total, paginated } = paginate(sorted, inspectionsPage);
    const page = inspectionsPage; const setPage = setInspectionsPage;
    return (
      <div className="tab-section">
        <div className="section-toolbar">
          <h2>Inspections</h2>
          <div className="toolbar-right">
            <input type="text" className="legal-search" placeholder="Search inspections..." value={inspectionsSearch} onChange={e => setInspectionsSearch(e.target.value)} />
            <button className="btn-primary" onClick={() => openInspectionPanel()}>+ New Inspection</button>
          </div>
        </div>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr>
                <SortHeader label="Property" colKey="property_name" {...inspSort} onSort={inspSort.toggle} />
                <SortHeader label="Type" colKey="inspection_type" {...inspSort} onSort={inspSort.toggle} />
                <SortHeader label="Status" colKey="status" {...inspSort} onSort={inspSort.toggle} />
                <SortHeader label="Scheduled" colKey="scheduled_date" {...inspSort} onSort={inspSort.toggle} />
                <SortHeader label="Inspector" colKey="inspector" {...inspSort} onSort={inspSort.toggle} />
                <th>Follow-up</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr><td colSpan="7" className="empty-row">No inspections found</td></tr>
              ) : paginated.map(i => (
                <tr key={i.id}>
                  <td>{i.property_name || '--'}</td>
                  <td>{(i.inspection_type || '').replace(/_/g, ' ')}</td>
                  <td><StatusBadge status={i.status} /></td>
                  <td>{fmtDate(i.scheduled_date)}</td>
                  <td>{i.inspector || '--'}</td>
                  <td>{i.follow_up_required ? <span style={{ color: '#EF4444', fontWeight: 600 }}>Yes</span> : <span style={{ color: '#6B7280' }}>No</span>}</td>
                  <td className="action-cell">
                    <button className="icon-btn" title="Edit" onClick={() => openInspectionPanel(i)}>✏️</button>
                    <button className="icon-btn" title="Delete" onClick={() => deleteInspection(i.id)}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} total={total} setPage={setPage} />

        {/* Side Panel */}
        {inspectionPanel && (
          <div className="legal-side-panel-overlay" onClick={() => setInspectionPanel(false)}>
            <div className="legal-side-panel" onClick={e => e.stopPropagation()}>
              <div className="sp-header">
                <h3>{editingInspection ? 'Edit Inspection' : 'New Inspection'}</h3>
                <button className="sp-close" onClick={() => setInspectionPanel(false)}>×</button>
              </div>
              <div className="sp-body">
                <label>Property
                  <select value={inspectionForm.property_id || ''} onChange={e => setInspectionForm({ ...inspectionForm, property_id: e.target.value })}>
                    <option value="">Select Property</option>
                    {properties.map(p => <option key={p.id} value={p.id}>{p.name || p.address}</option>)}
                  </select>
                </label>
                <label>Inspection Type
                  <select value={inspectionForm.inspection_type || 'routine'} onChange={e => setInspectionForm({ ...inspectionForm, inspection_type: e.target.value })}>
                    <option value="routine">Routine</option>
                    <option value="move_in">Move-In</option>
                    <option value="move_out">Move-Out</option>
                    <option value="safety">Safety</option>
                    <option value="complaint">Complaint</option>
                    <option value="follow_up">Follow-Up</option>
                  </select>
                </label>
                <label>Status
                  <select value={inspectionForm.status || 'scheduled'} onChange={e => setInspectionForm({ ...inspectionForm, status: e.target.value })}>
                    <option value="scheduled">Scheduled</option>
                    <option value="in_progress">In Progress</option>
                    <option value="complete">Complete</option>
                    <option value="overdue">Overdue</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </label>
                <label>Scheduled Date<input type="date" value={inspectionForm.scheduled_date || ''} onChange={e => setInspectionForm({ ...inspectionForm, scheduled_date: e.target.value })} /></label>
                <label>Inspector<input type="text" value={inspectionForm.inspector || ''} onChange={e => setInspectionForm({ ...inspectionForm, inspector: e.target.value })} /></label>
                <label className="checkbox-label">
                  <input type="checkbox" checked={inspectionForm.follow_up_required || false} onChange={e => setInspectionForm({ ...inspectionForm, follow_up_required: e.target.checked })} />
                  Follow-up Required
                </label>
                <label>Notes<textarea rows="3" value={inspectionForm.notes || ''} onChange={e => setInspectionForm({ ...inspectionForm, notes: e.target.value })} /></label>
              </div>
              <div className="sp-footer">
                <button className="btn-primary" onClick={saveInspection}>Save</button>
                <button className="btn-secondary" onClick={() => setInspectionPanel(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  /* (Document Templates merged into Documents tab) */

  /* ═══════════════════════════════════════════════════════════════
     MAIN RENDER
     ═══════════════════════════════════════════════════════════════ */
  const renderActiveTab = () => {
    switch (activeTab) {
      case 'Dashboard': return renderDashboard();
      case 'Tenant Cards': return renderTenantCards();
      case 'Legal Cases': return renderCases();
      case 'Compliance': return renderCompliance();
      case 'Documents': return renderDocuments();
      case 'Tenant Scoring': return renderTenantScoring();
      case 'Notices & Evictions': return renderNoticesEvictions();
      case 'Lease Renewals': return renderRenewals();
      case 'Inspections': return renderInspections();
      default: return renderDashboard();
    }
  };

  return (
    <div className="legal-page">
      <Navbar selected="Legal" />
      <div className="legal-workspace">
        {/* ── Left Sidebar ── */}
        <aside className="legal-sidebar">
          <div className="legal-sidebar-header">
            <span className="legal-sidebar-title">Legal</span>
          </div>
          <div className="legal-sidebar-list">
            {TABS.map(tab => (
              <button
                key={tab.id}
                className={`legal-sidebar-item${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
              >
                <span className="legal-sidebar-item-icon">{tab.icon}</span>
                <span className="legal-sidebar-item-name">{tab.label}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* ── Main Content ── */}
        <div className="legal-main">
          <div className="legal-page-header">
            <div className="legal-title-block">
              <h1 className="legal-page-title">{activeTab}</h1>
              <p className="legal-page-subtitle">Manage legal cases, compliance, evictions, and tenant risk</p>
            </div>
            <div className="legal-header-actions">
              <select className="property-filter-select" value={propertyFilter} onChange={e => setPropertyFilter(e.target.value)}>
                <option value="">All Properties</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name || p.address}</option>)}
              </select>
            </div>
          </div>

          <div className="legal-tab-content">
            {loading && activeTab === 'Dashboard' ? (
              <div className="loading-state">Loading...</div>
            ) : (
              renderActiveTab()
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalDashboard;
